import { DatePicker, Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { ensureDate } from '../../../utils/dateCalculations';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { validationSchema } from './threemaBroadcastForm.schema';

const CreateThreemaBroadcastsMutationDocument = graphql(`
  mutation CreateThreemaBroadcastss($input: [ThreemaBroadcastCreateInput!]!) {
    createThreemaBroadcasts(input: $input) {
      threemaBroadcasts {
        uuid
        __typename
      }
    }
  }
`);
const UpdateThreemaBroadcastsMutationDocument = graphql(`
  mutation UpdateThreemaBroadcastss($update: ThreemaBroadcastUpdateInput!, $where: ThreemaBroadcastWhere!) {
    updateThreemaBroadcasts(update: $update, where: $where) {
      threemaBroadcasts {
        uuid
        __typename
        apiToken
        threemaId
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              notAfter
              notBefore
            }
          }
        }
      }
    }
  }
`);

const SettingsThreemaBroadcastFormQueryDocument = graphql(`
  query SettingsThreemaBroadcastFormData($uuid: ID!) {
    threemaBroadcasts(where: { uuid: $uuid }) {
      uuid
      __typename
      threemaId
      apiToken
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notAfter
            notBefore
            version
            contractRef
          }
        }
      }
    }
  }
`);

type ThreemaValuesType = {
  broadcastId: string | null;
  broadcastApiToken: string | null;
  notBefore: string;
  notAfter?: string;
};

type ThreemaBroadcastFormProps = { onClose: () => void };

export const ThreemaBroadcastForm = ({ onClose }: ThreemaBroadcastFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { organizationProducts } = useContext(OrganizationConfigContext);

  const broadcastUUid = organizationProducts?.find((d) => d.__typename === 'ThreemaBroadcast')?.uuid;

  const [{ data: queryData }] = useQuery({
    query: SettingsThreemaBroadcastFormQueryDocument,
    variables: {
      uuid: broadcastUUid ?? '',
    },
  });

  const currentBooking = queryData?.threemaBroadcasts[0];
  const currentBookingEdge = currentBooking?.organizationConnection.edges[0];

  const permissions = usePermissionChecker();

  const [, updateThreemaBroadcasts] = useMutation(UpdateThreemaBroadcastsMutationDocument);
  const [, createThreemaBroadcasts] = useMutation(CreateThreemaBroadcastsMutationDocument);

  const initialValues: ThreemaValuesType = {
    broadcastId: currentBooking?.threemaId ?? '',
    broadcastApiToken: currentBooking?.apiToken ?? '',
    notBefore: ensureDate(currentBookingEdge?.properties.notBefore ?? null).toUTCString(),
    notAfter: currentBookingEdge?.properties.notAfter
      ? ensureDate(currentBookingEdge?.properties.notAfter).toUTCString()
      : undefined,
  };

  const onSubmit = async (values: ThreemaValuesType) => {
    if (permissions?.canUpdateThreemaBroadcast({ uuid: organizationUuid })) {
      if (broadcastUUid === undefined) {
        const resp = await createThreemaBroadcasts({
          input: [
            {
              apiToken: values.broadcastApiToken,
              threemaId: values.broadcastId,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notAfter: values.notAfter ?? '',
                    notBefore: values.notBefore,
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateThreemaBroadcasts({
          update: {
            apiToken: values.broadcastApiToken,
            threemaId: values.broadcastId,
            organization: {
              update: {
                edge: {
                  notAfter: values.notAfter ?? null,
                  notBefore: values.notBefore,
                },
              },
            },
          },
          where: { uuid: broadcastUUid },
        });

        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, handleBlur, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedFromDate')}
                    onChange={async (e) => await setFieldValue('notBefore', e?.toUTCString())}
                    value={ensureDate(values.notBefore)}
                    name={'notBefore'}
                    showMonthYearDropdown
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('institution.bookedUntilDate')}
                    onChange={async (e) => await setFieldValue('notAfter', e?.toUTCString())}
                    value={values.notAfter ? ensureDate(values.notAfter) : undefined}
                    name={'notAfter'}
                    required={false}
                    showMonthYearDropdown
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                <Input
                  label={t('settings.threemaBroadcastId')}
                  name={'broadcastId'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.broadcastId?.toString()}
                  error={errors.broadcastId}
                />
              </GridRow>
              <GridRow spacingTop='s'>
                <Input
                  label={t('settings.threemaBroadcastApiToken')}
                  name={'broadcastApiToken'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.broadcastApiToken?.toString()}
                  error={errors.broadcastApiToken}
                />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
